import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Button, useToast, Spinner, Table, Thead, Tbody, Tr, Th, Td,
  IconButton, HStack, Text, useDisclosure,
  Modal, ModalOverlay, ModalContent, ModalHeader, ModalFooter, ModalBody, ModalCloseButton,
  FormControl, FormLabel, Input, Textarea, VStack
} from '@chakra-ui/react';
import { MdAdd, MdEdit, MdDelete } from 'react-icons/md';
import { getAuthToken } from '../../utils/auth';
import { config } from '../../config';

// API Functions specific to templates
const API_BASE_URL = config.apiUrl;

const apiCall = async (endpoint, method = 'GET', body = null, token) => {
    const options = {
        method,
        headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
        },
    };
    if (body) {
        options.body = JSON.stringify(body);
    }
    const response = await fetch(`${API_BASE_URL}${endpoint}`, options);
    if (!response.ok) {
        const errData = await response.json();
        throw new Error(errData.message || 'API request failed');
    }
    return response.json();
};


function TemplateManagement() {
  const [templates, setTemplates] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const toast = useToast();
  const token = getAuthToken();

  const fetchTemplates = useCallback(async () => {
    setIsLoading(true);
    try {
      const data = await apiCall('/email-templates', 'GET', null, token);
      setTemplates(data);
    } catch (error) {
      toast({ title: "Error fetching templates", description: error.message, status: "error" });
    } finally {
      setIsLoading(false);
    }
  }, [token, toast]);

  useEffect(() => {
    fetchTemplates();
  }, [fetchTemplates]);

  const handleSave = async (templateData) => {
    try {
      if (templateData.id) { // Update existing
        await apiCall(`/email-templates/${templateData.id}`, 'PUT', templateData, token);
        toast({ title: "Template Updated", status: "success" });
      } else { // Create new
        await apiCall('/email-templates', 'POST', templateData, token);
        toast({ title: "Template Created", status: "success" });
      }
      fetchTemplates(); // Refresh the list
      onClose();
    } catch (error) {
      toast({ title: "Save failed", description: error.message, status: "error" });
    }
  };

  const handleDelete = async (templateId) => {
    if (window.confirm('Are you sure you want to delete this template?')) {
        try {
            await apiCall(`/email-templates/${templateId}`, 'DELETE', null, token);
            toast({ title: "Template Deleted", status: "success" });
            fetchTemplates(); // Refresh the list
        } catch (error) {
            toast({ title: "Delete failed", description: error.message, status: "error" });
        }
    }
  };

  const openModal = (template = null) => {
    setSelectedTemplate(template);
    onOpen();
  };

  if (isLoading) return <Spinner />;

  return (
    <Box>
      <Button leftIcon={<MdAdd />} colorScheme="blue" onClick={() => openModal()}>
        Create New Template
      </Button>
      <Table mt={6}>
        <Thead>
          <Tr>
            <Th>Template Name</Th>
            <Th>Subject</Th>
            <Th>Actions</Th>
          </Tr>
        </Thead>
        <Tbody>
          {templates.map((template) => (
            <Tr key={template.id}>
              <Td>{template.name}</Td>
              <Td>{template.subject}</Td>
              <Td>
                <HStack>
                  <IconButton icon={<MdEdit />} aria-label="Edit" onClick={() => openModal(template)} />
                  <IconButton icon={<MdDelete />} aria-label="Delete" colorScheme="red" onClick={() => handleDelete(template.id)} />
                </HStack>
              </Td>
            </Tr>
          ))}
        </Tbody>
      </Table>
      
      {/* Save/Edit Modal */}
      <TemplateFormModal 
        isOpen={isOpen} 
        onClose={onClose} 
        onSave={handleSave} 
        template={selectedTemplate} 
      />
    </Box>
  );
}

// Sub-component for the form modal to keep things clean
function TemplateFormModal({ isOpen, onClose, onSave, template }) {
    const [name, setName] = useState('');
    const [subject, setSubject] = useState('');
    const [body, setBody] = useState('');

    useEffect(() => {
        setName(template?.name || '');
        setSubject(template?.subject || '');
        setBody(template?.body || '');
    }, [template]);

    const handleSaveClick = () => {
        onSave({ id: template?.id, name, subject, body });
    };

    return (
        <Modal isOpen={isOpen} onClose={onClose} size="xl">
            <ModalOverlay />
            <ModalContent>
                <ModalHeader>{template ? 'Edit' : 'Create'} Template</ModalHeader>
                <ModalCloseButton />
                <ModalBody>
                    <VStack spacing={4}>
                        <FormControl isRequired>
                            <FormLabel>Template Name</FormLabel>
                            <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g., Planned Maintenance Notice" />
                        </FormControl>
                        <FormControl isRequired>
                            <FormLabel>Subject</FormLabel>
                            <Input value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="Email subject line" />
                        </FormControl>
                        <FormControl isRequired>
                            <FormLabel>Body</FormLabel>
                            <Textarea value={body} onChange={(e) => setBody(e.target.value)} placeholder="Email body content. Use {{name}} for personalization." rows={10} />
                        </FormControl>
                    </VStack>
                </ModalBody>
                <ModalFooter>
                    <Button variant="ghost" mr={3} onClick={onClose}>Cancel</Button>
                    <Button colorScheme="blue" onClick={handleSaveClick}>Save Template</Button>
                </ModalFooter>
            </ModalContent>
        </Modal>
    );
}

export default TemplateManagement;